/**
 * Deterministic Collectible Spawn Configuration
 *
 * Defines exactly 45 fixed collectible spawn positions across all zones.
 * These positions are deterministic - the same platforms always have collectibles.
 * This ensures fair, comparable runs for all players.
 *
 * Distribution:
 * - Zone 1: 15 collectibles (most accessible, 33%)
 * - Zone 2: 8 collectibles (18%)
 * - Zone 3: 6 collectibles (13%)
 * - Zone 4: 6 collectibles (13%)
 * - Zone 5: 5 collectibles (11%)
 * - Zone 6: 5 collectibles (hardest, 11%)
 * Total: 45 collectibles (100%)
 */

import { CollectibleType } from '../entities/Collectible';

export interface CollectibleSpawn {
  platformIndex: number; // Which platform (0-104 for 105 platforms)
  type: CollectibleType; // What collectible type to spawn
  zone: number; // Expected zone (for validation)
}

/**
 * Fixed list of exactly 45 collectible spawns
 * Platform indices are distributed across the level (105 platforms total)
 * Types are appropriate for each zone
 */
export const FIXED_COLLECTIBLE_SPAWNS: CollectibleSpawn[] = [
  // Zone 1 (platforms ~0-20): 15 collectibles - Forest Floor variety
  { platformIndex: 1, type: CollectibleType.FIREFLY, zone: 1 },
  { platformIndex: 3, type: CollectibleType.DEWDROP, zone: 1 },
  { platformIndex: 5, type: CollectibleType.BUTTERFLY, zone: 1 },
  { platformIndex: 7, type: CollectibleType.BEETLE, zone: 1 },
  { platformIndex: 9, type: CollectibleType.SNAIL, zone: 1 },
  { platformIndex: 11, type: CollectibleType.FROG, zone: 1 },
  { platformIndex: 13, type: CollectibleType.MUSHROOM, zone: 1 },
  { platformIndex: 15, type: CollectibleType.FIREFLY, zone: 1 },
  { platformIndex: 17, type: CollectibleType.DEWDROP, zone: 1 },
  { platformIndex: 19, type: CollectibleType.BUTTERFLY, zone: 1 },
  { platformIndex: 21, type: CollectibleType.BEETLE, zone: 1 },
  { platformIndex: 23, type: CollectibleType.SNAIL, zone: 1 },
  { platformIndex: 25, type: CollectibleType.FROG, zone: 1 },
  { platformIndex: 27, type: CollectibleType.MUSHROOM, zone: 1 },
  { platformIndex: 29, type: CollectibleType.FIREFLY, zone: 1 },

  // Zone 2 (platforms ~21-40): 8 collectibles - Tree Canopy
  { platformIndex: 32, type: CollectibleType.BIRD, zone: 2 },
  { platformIndex: 35, type: CollectibleType.MOTH, zone: 2 },
  { platformIndex: 38, type: CollectibleType.BIRD, zone: 2 },
  { platformIndex: 41, type: CollectibleType.MOTH, zone: 2 },
  { platformIndex: 44, type: CollectibleType.BIRD, zone: 2 },
  { platformIndex: 47, type: CollectibleType.MOTH, zone: 2 },
  { platformIndex: 50, type: CollectibleType.BIRD, zone: 2 },
  { platformIndex: 53, type: CollectibleType.MOTH, zone: 2 },

  // Zone 3 (platforms ~41-60): 6 collectibles - Sky Layers
  { platformIndex: 56, type: CollectibleType.FEATHER, zone: 3 },
  { platformIndex: 59, type: CollectibleType.FEATHER, zone: 3 },
  { platformIndex: 62, type: CollectibleType.FEATHER, zone: 3 },
  { platformIndex: 65, type: CollectibleType.FEATHER, zone: 3 },
  { platformIndex: 68, type: CollectibleType.FEATHER, zone: 3 },
  { platformIndex: 71, type: CollectibleType.FEATHER, zone: 3 },

  // Zone 4 (platforms ~61-80): 6 collectibles - Starlit Night
  { platformIndex: 74, type: CollectibleType.STAR, zone: 4 },
  { platformIndex: 77, type: CollectibleType.STARDUST, zone: 4 },
  { platformIndex: 80, type: CollectibleType.STAR, zone: 4 },
  { platformIndex: 83, type: CollectibleType.STARDUST, zone: 4 },
  { platformIndex: 86, type: CollectibleType.STAR, zone: 4 },
  { platformIndex: 89, type: CollectibleType.STARDUST, zone: 4 },

  // Zone 5 (platforms ~81-95): 5 collectibles - Golden Summit
  { platformIndex: 92, type: CollectibleType.COIN, zone: 5 },
  { platformIndex: 94, type: CollectibleType.GEM, zone: 5 },
  { platformIndex: 96, type: CollectibleType.SPARKLE, zone: 5 },
  { platformIndex: 98, type: CollectibleType.COIN, zone: 5 },
  { platformIndex: 100, type: CollectibleType.GEM, zone: 5 },

  // Zone 6 (platforms ~96-104): 5 collectibles - Ultimate Challenge
  { platformIndex: 99, type: CollectibleType.COSMIC, zone: 5 }, // Zone 5-6 transition
  { platformIndex: 101, type: CollectibleType.ENERGY_ORB, zone: 6 },
  { platformIndex: 102, type: CollectibleType.COSMIC, zone: 6 },
  { platformIndex: 103, type: CollectibleType.PORTAL, zone: 6 },
  { platformIndex: 104, type: CollectibleType.ENERGY_ORB, zone: 6 },
];

/**
 * Verify the configuration is valid
 */
export function validateCollectibleSpawns(): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check count
  if (FIXED_COLLECTIBLE_SPAWNS.length !== 45) {
    errors.push(`Expected 45 collectibles, got ${FIXED_COLLECTIBLE_SPAWNS.length}`);
  }

  // Check for duplicate platform indices
  const indices = new Set<number>();
  for (const spawn of FIXED_COLLECTIBLE_SPAWNS) {
    if (indices.has(spawn.platformIndex)) {
      errors.push(`Duplicate platform index: ${spawn.platformIndex}`);
    }
    indices.add(spawn.platformIndex);
  }

  // Check platform indices are in valid range (assuming 105 platforms, indices 0-105)
  for (const spawn of FIXED_COLLECTIBLE_SPAWNS) {
    if (spawn.platformIndex < 0 || spawn.platformIndex > 105) {
      errors.push(`Invalid platform index: ${spawn.platformIndex} (must be 0-105)`);
    }
  }

  return { valid: errors.length === 0, errors };
}
